import { MapPin, Handshake, Hammer, HeartHandshake, Clock } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import ContactCta from "@/components/ContactCta";
import { site } from "@/data/site";

const values = [
    {
        icon: Hammer,
        title: "Ordentligt håndværk",
        text: "Jeg sætter en ære i godt håndværk og ordentligt arbejde. Kvalitetsmaterialer, lige linjer og detaljer der også holder om fem og ti år.",
    },
    {
        icon: HeartHandshake,
        title: "God dialog",
        text: "Det er mig, du møder på pladsen, i telefonen og i mailen. Ingen mellemled — kun ærlig snak om pris, plan og forventninger.",
    },
    {
        icon: MapPin,
        title: "Lokal i Silkeborg og omegn",
        text: "Jeg bor og arbejder i Silkeborg. Kort vej til jer, kort vej til et svar — og kort vej ud til projektet.",
    },
    {
        icon: Handshake,
        title: "Løsninger du bliver glad for",
        text: "For mig handler det ikke bare om at blive færdig hurtigt. Det handler om løsninger, du bliver glad for i længden — derfor sætter vi rammerne sammen, inden vi går i gang.",
    },
];

export default function Om() {
    return (
        <div data-testid="page-om">
            <PageHeader
                overline="Om NS Tømrer"
                title="Jeg hedder Nicklas — din lokale tømrer i Silkeborg og omegn."
                subtitle="NS Tømrer og Ejendomsservice er én mand med stolthed i faget, et godt sæt værktøj og et ønske om at gøre håndværk personligt igen."
            />

            <section className="py-16 sm:py-24 bg-[var(--ns-bg)]">
                <div className="container-ns">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                        <div className="lg:col-span-5 order-2 lg:order-1">
                            <div className="aspect-[4/5] overflow-hidden rounded-md border border-[var(--ns-border)] bg-white sticky top-24">
                                <img
                                    src={site.portrait}
                                    alt="Nicklas Sørensen — tømrer i Silkeborg"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>

                        <div className="lg:col-span-7 order-1 lg:order-2 space-y-6 text-[var(--ns-text)]">
                            <div className="overline">Ejeren</div>
                            <h2 className="heading-accent font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
                                Jeg hedder Nicklas, og jeg driver NS Tømrer
                                & Ejendomsservice.
                            </h2>
                            <p className="text-lg text-[var(--ns-muted)] leading-relaxed">
                                Jeg hjælper dagligt kunder i Silkeborg og omegn
                                med både små og større tømreropgaver. Det kan
                                være alt fra en 80+ m² sommerhusterrasse, en
                                totalrenovering af entré og bryggers — til
                                udskiftning af lister, nye vinduer eller en
                                mindre serviceopgave der bare skal gøres
                                ordentligt.
                            </p>
                            <p className="text-lg text-[var(--ns-muted)] leading-relaxed">
                                For mig handler det ikke bare om at blive
                                færdig hurtigt, men om at levere ordentligt
                                håndværk, god dialog og løsninger, du bliver
                                glad for i længden. Jeg sætter en ære i godt
                                håndværk og ordentligt arbejde.
                            </p>
                            <blockquote className="border-l-4 border-[var(--ns-blue)] pl-5 py-2 my-8">
                                <p className="font-display font-extrabold text-xl sm:text-2xl tracking-tight text-[var(--ns-text)] leading-snug">
                                    &quot;Jeg Nicklas Sørensen kommer gerne
                                    ud, tager en snak og giver et helt
                                    uforpligtende tilbud.&quot;
                                </p>
                                <footer className="mt-3 text-sm text-[var(--ns-muted)]">
                                    — Nicklas Sørensen, ejer
                                </footer>
                            </blockquote>
                            <p className="text-lg text-[var(--ns-muted)] leading-relaxed">
                                Jeg kommer gerne ud og kigger, før vi
                                overhovedet snakker pris. Det giver jer et
                                reelt tilbud — og mig en chance for at forstå
                                præcis, hvad I drømmer om. Skriv eller ring, så
                                tager vi en snak og finder en løsning, der
                                passer jer 👌
                            </p>

                            <div className="flex flex-wrap items-center gap-3 pt-2">
                                <div className="inline-flex items-center gap-2 bg-[var(--ns-blue-soft)] text-[var(--ns-blue)] px-4 py-2 rounded-full">
                                    <span className="w-2 h-2 rounded-full bg-[var(--ns-blue)] animate-pulse" />
                                    <span className="font-display font-bold text-sm">
                                        {site.availabilityNote}
                                    </span>
                                </div>
                                <div className="inline-flex items-center gap-2 text-sm text-[var(--ns-muted)]">
                                    <Clock className="w-4 h-4" />
                                    Hverdage kl. 07 – 17
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 sm:py-24 bg-white border-y border-[var(--ns-border)]">
                <div className="container-ns">
                    <div className="max-w-2xl mb-12">
                        <div className="overline mb-4">Sådan arbejder jeg</div>
                        <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
                            Fire ting jeg aldrig går på kompromis med.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {values.map((v, i) => {
                            const Icon = v.icon;
                            return (
                                <div
                                    key={i}
                                    data-testid={`value-${i}`}
                                    className="card-ns p-7"
                                >
                                    <span className="w-12 h-12 rounded-md bg-[var(--ns-blue-soft)] text-[var(--ns-blue)] grid place-items-center mb-5">
                                        <Icon
                                            className="w-6 h-6"
                                            strokeWidth={1.7}
                                        />
                                    </span>
                                    <h3 className="font-display font-extrabold text-lg mb-2 tracking-tight">
                                        {v.title}
                                    </h3>
                                    <p className="text-[var(--ns-muted)] text-[15px] leading-relaxed">
                                        {v.text}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <ContactCta />
        </div>
    );
}
